const SCROLL_BUFFER = 10;
const SCROLL_FADE_START = 50;
const SCROLL_FADE_END = 100;
const SCROLL_INDICATOR_THRESHOLD = 200;

class AutoScroller {
    private readonly setScrollDownOpacity: (opacity: number) => void;
    private readonly setNewMessageIndicatorVisible: (visible: boolean) => void;
    private isScrolledUp: boolean;
    private lastScrollTop: number;

    constructor(
        setScrollDownOpacity: (opacity: number) => void,
        setNewMessageIndicatorVisible: (visible: boolean) => void,
    ) {
        this.setScrollDownOpacity = setScrollDownOpacity;
        this.setNewMessageIndicatorVisible = setNewMessageIndicatorVisible;
        this.isScrolledUp = false;
        this.lastScrollTop = 0;
    }

    onScroll = (
        messagesDiv: HTMLDivElement
    ) => {
        const scrollingUp = messagesDiv.scrollTop < this.lastScrollTop;
        const aboveBuffer = this.isAboveScrollBuffer(messagesDiv);
        const aboveThreshold = this.getScrolledAboveThreshold(messagesDiv) > 0;
        if (!aboveBuffer) {
            this.isScrolledUp = false;
        } else if (scrollingUp) {
            this.isScrolledUp = true;
        }
        if (!aboveThreshold) {
            this.setNewMessageIndicatorVisible(false);
        }

        this.lastScrollTop = messagesDiv.scrollTop;
        this.setOpacities(messagesDiv);
    };

    onDomChanged = (messagesDiv: HTMLDivElement) => {
        if (!this.isScrolledUp) {
            this.scrollToBottom(messagesDiv);
        }

        this.lastScrollTop = messagesDiv.scrollTop;
        this.setOpacities(messagesDiv);
    };

    scrollToBottom = (messagesDiv: HTMLDivElement) => {
        messagesDiv.scroll({ top: messagesDiv.scrollHeight, behavior: 'smooth' });
        this.setScrollDownOpacity(0);
    };

    isAboveScrollBuffer = (
        messagesDiv: HTMLDivElement
    ) => {
        if (!messagesDiv) return false;
        return (messagesDiv.scrollHeight - messagesDiv.clientHeight - messagesDiv.scrollTop) > SCROLL_BUFFER;
    };

    newMessageReceived = (
        messagesDiv: HTMLDivElement | null
    ) => {
        if (messagesDiv) {
            const aboveThreshold = this.getScrolledAboveThreshold(messagesDiv) > 0;
            this.setNewMessageIndicatorVisible(aboveThreshold);
        }
    };

    private getScrolledAboveThreshold = (
        messagesDiv: HTMLDivElement
    ) => {
        return Math.max(0, messagesDiv.scrollHeight - messagesDiv.clientHeight - messagesDiv.scrollTop - SCROLL_INDICATOR_THRESHOLD);
    };

    private setOpacities = (
        messagesDiv: HTMLDivElement
    ) => {
        const scrolledAboveThreshold = this.getScrolledAboveThreshold(messagesDiv);

        this.setScrollDownOpacity(this.getOpacity(scrolledAboveThreshold));
    };

    private getOpacity = (
        fadePosition: number
    ) => {
        if (fadePosition < SCROLL_FADE_START) {
            return 0;
        } else if (fadePosition > SCROLL_FADE_END) {
            return 1;
        } else {
            return (fadePosition - SCROLL_FADE_START) /
                (SCROLL_FADE_END - SCROLL_FADE_START);
        }
    };
}

export default AutoScroller;
